import { useQuery } from "@tanstack/react-query";
import api, { ApiCatalog } from "@/api";

const ELA_TAXONOMY_ID = "cmcwn93jy0009pd90g6jakuw3";
const SOCIAL_STUDIES_TAXONOMY_ID_2011 = "cmcwn93hs0000pd90f8o80oj9";
const SOCIAL_STUDIES_TAXONOMY_ID_2021 = "cmcwn93py000rpd908dlxdd60";
const SOCIAL_STUDIES_2021_ITEM_TO_APPEND = "cmcxoxidt000n36906428c81c";
const SCIENCE_TAXONOMY_ID = "cmcwn93im0002pd905b3x3rf4";

export const useTaxonomyConceptsQuery = () => {
  const elaResults = useQuery({
    queryKey: [ApiCatalog.documentViewmodel.key, ELA_TAXONOMY_ID],
    queryFn: () =>
      api().get(`${ApiCatalog.documentViewmodel.endpoint}`, {
        urlReplacers: [{ id: ELA_TAXONOMY_ID }],
      }),
  });

  // Certica Taxonomy has two Social Studies documents,
  // so we have to call them separately and merge the results below.
  const firstSSResults = useQuery({
    queryKey: [
      ApiCatalog.documentViewmodel.key,
      SOCIAL_STUDIES_TAXONOMY_ID_2011,
    ],
    queryFn: () =>
      api().get(`${ApiCatalog.documentViewmodel.endpoint}`, {
        urlReplacers: [{ id: SOCIAL_STUDIES_TAXONOMY_ID_2011 }],
      }),
  });

  const secondSSResults = useQuery({
    queryKey: [
      ApiCatalog.documentViewmodel.key,
      SOCIAL_STUDIES_TAXONOMY_ID_2021,
    ],
    queryFn: () =>
      api().get(`${ApiCatalog.documentViewmodel.endpoint}`, {
        urlReplacers: [{ id: SOCIAL_STUDIES_TAXONOMY_ID_2021 }],
      }),
  });

  const scienceResults = useQuery({
    queryKey: [ApiCatalog.documentViewmodel.key, SCIENCE_TAXONOMY_ID],
    queryFn: () =>
      api().get(`${ApiCatalog.documentViewmodel.endpoint}`, {
        urlReplacers: [{ id: SCIENCE_TAXONOMY_ID }],
      }),
  });

  const isLoading =
    elaResults.isLoading ||
    firstSSResults.isLoading ||
    secondSSResults.isLoading ||
    scienceResults.isLoading;

  const mergeSSResults = (
    objA: Record<string, any>,
    objB: Record<string, any>,
    targetItemId: string,
    childIdToAdd: string,
  ) => {
    // Merge items from both documents
    const mergedItems = {
      ...objA["items"],
      ...objB["items"],
    };

    // Include the merged item root id in the first object's
    // immediate children so the modal can nest them together.
    if (targetItemId && childIdToAdd && mergedItems[targetItemId]) {
      mergedItems[targetItemId] = {
        ...mergedItems[targetItemId],
        children: [...(mergedItems[targetItemId].children || []), childIdToAdd],
      };
    }

    return {
      ...objA,
      items: mergedItems,
    };
  };
  const sSResults =
    firstSSResults.isLoading || secondSSResults.isLoading
      ? undefined
      : mergeSSResults(
          firstSSResults.data as Record<string, any>,
          secondSSResults.data as Record<string, any>,
          SOCIAL_STUDIES_TAXONOMY_ID_2011,
          SOCIAL_STUDIES_2021_ITEM_TO_APPEND,
        );

  return {
    data: {
      ela: elaResults.data,
      socialStudies: sSResults,
      science: scienceResults.data,
    },
    isLoading,
  };
};
